import { useEffect, useRef, useState } from "react";
import Card from "components/card";
import Navbar from "components/navbar";
import Loader from "components/loader/loader";
import { Switch, Text, Box, Divider } from "@chakra-ui/react";
import { t } from "i18next";
import { getAppConfig, createAppConfig, updateAppConfig } from "services/customAPI";
import { toast } from "react-toastify";
import { errorToast, successToast } from "utils/commons";
import { Link } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import {
    MdOutlineAttachMoney,
    MdOutlinePhoneIphone,
    MdOutlineCreditCard,
} from "react-icons/md";

const PAYMENT_OPTIONS = [
    {
        key: "cash",
        label: "Dinheiro",
        description: "Pagamento em espécie",
        icon: <MdOutlineAttachMoney className="h-6 w-6" />,
        color: "green",
        iconBg: "bg-green-500/15 text-green-400",
        activeBorder: "border-green-500/30",
        activeGlow: "shadow-green-500/10",
    },
    {
        key: "online",
        label: "Pagamento Online",
        description: "Pagamento pelo aplicativo (Mercado Pago)",
        icon: <MdOutlinePhoneIphone className="h-6 w-6" />,
        color: "cyan",
        iconBg: "bg-cyan-500/15 text-cyan-400",
        activeBorder: "border-cyan-500/30",
        activeGlow: "shadow-cyan-500/10",
    },
    {
        key: "card_machine",
        label: "Maquininha de Cartão",
        description: "Cartão de crédito ou débito via maquininha do motorista",
        icon: <MdOutlineCreditCard className="h-6 w-6" />,
        color: "purple",
        iconBg: "bg-purple-500/15 text-purple-400",
        activeBorder: "border-purple-500/30",
        activeGlow: "shadow-purple-500/10",
    },
];

const CONFIG_NAME = "AVAILABLE_PAYMENT_METHODS";

function PaymentMethods() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [selected, setSelected] = useState<Set<string>>(new Set(["cash", "online"]));
    const [configExists, setConfigExists] = useState(false);
    const [hydrated, setHydrated] = useState(false);
    const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const response = await getAppConfig(CONFIG_NAME);
                const configValue = response?.data?.value ?? "";
                if (configValue) {
                    const methods = configValue.split(",").map((s: string) => s.trim());
                    setSelected(new Set(methods));
                    setConfigExists(true);
                }
            } catch (error: any) {
                console.log("Payment methods config not found, using defaults");
            } finally {
                setLoading(false);
                setHydrated(true);
            }
        };
        fetchConfig();
    }, []);

    const toggleMethod = (key: string) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                if (next.size <= 1) {
                    toast.warning("Selecione pelo menos um método de pagamento.");
                    return prev;
                }
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const handleSave = async (showToast = true) => {
        setSaving(true);
        try {
            const value = Array.from(selected).join(",");
            const configData = {
                name: CONFIG_NAME,
                value,
                placeHolder: "Métodos de pagamento disponíveis na plataforma",
                active: true,
            };

            if (configExists) {
                await updateAppConfig(CONFIG_NAME, configData);
            } else {
                await createAppConfig(configData);
                setConfigExists(true);
            }
            if (showToast) successToast("Métodos de pagamento atualizados com sucesso!");
        } catch (error: any) {
            console.error("Error saving payment methods:", error);
            if (showToast) errorToast("Erro ao salvar métodos de pagamento.");
        } finally {
            setSaving(false);
        }
    };

    // Auto-save on change
    useEffect(() => {
        if (!hydrated) return;
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }
        saveTimeoutRef.current = setTimeout(() => {
            handleSave(false);
        }, 500);
        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, [hydrated, selected, handleSave]);

    return (
        <>
            <Navbar flag={false} brandText={t("Settings")} />
            <Link
                to="/admin/settings"
                className="mt-3 mb-1 inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
            >
                <FaArrowLeft className="h-3 w-3" />
                {t("Voltar para Configurações")}
            </Link>
            {loading ? (
                <Loader />
            ) : (
                <Card className="mt-2 p-6">
                    <header className="mb-6">
                        <div className="text-2xl font-bold text-white">
                            Métodos de Pagamento
                        </div>
                        <p className="mt-1 text-sm text-gray-400">
                            Selecione os métodos de pagamento disponíveis na plataforma.
                            Motoristas poderão ativar apenas os métodos habilitados aqui.
                        </p>
                    </header>

                    <div className="space-y-3">
                        {PAYMENT_OPTIONS.map((option) => {
                            const isActive = selected.has(option.key);
                            return (
                                <div
                                    key={option.key}
                                    onClick={() => toggleMethod(option.key)}
                                    className={`
                                        flex items-center justify-between rounded-2xl p-5 cursor-pointer
                                        transition-all duration-200 border
                                        ${isActive
                                            ? `bg-white/5 ${option.activeBorder} shadow-lg ${option.activeGlow}`
                                            : "bg-white/[0.02] border-white/5 opacity-60 hover:opacity-80"
                                        }
                                    `}
                                >
                                    <div className="flex items-center gap-4">
                                        <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${option.iconBg}`}>
                                            {option.icon}
                                        </div>
                                        <div>
                                            <div className="text-base font-semibold text-white">
                                                {option.label}
                                            </div>
                                            <div className="text-xs text-gray-400 mt-0.5">
                                                {option.description}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        {isActive && (
                                            <span className="text-xs font-medium text-green-400 bg-green-500/10 px-2 py-1 rounded-full">
                                                Ativo
                                            </span>
                                        )}
                                        <Switch
                                            isChecked={isActive}
                                            onChange={(e) => {
                                                e.stopPropagation();
                                                toggleMethod(option.key);
                                            }}
                                            colorScheme="cyan"
                                            size="lg"
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {saving && (
                        <div className="mt-4 flex items-center gap-2 text-xs text-gray-400">
                            <div className="h-3 w-3 rounded-full border-2 border-cyan-400 border-t-transparent animate-spin" />
                            Salvando...
                        </div>
                    )}

                    <div className="mt-6 bg-cyan-500/10 border border-cyan-500/20 rounded-xl px-4 py-3 flex items-start gap-2">
                        <span className="text-cyan-400 mt-0.5">ⓘ</span>
                        <p className="text-sm text-cyan-300/80">
                            As alterações são salvas automaticamente. Os passageiros verão apenas os métodos
                            ativos ao solicitar corrida, e os motoristas só poderão habilitar estes métodos
                            no cadastro.
                        </p>
                    </div>
                </Card>
            )}
        </>
    );
}

export default PaymentMethods;
